/**
 * Per-state registration term defaults for the Fees requests.
 *
 * Used only when the form has no explicit registration term. Most states register
 * passenger vehicles for 12 months, but the states below issue nothing shorter, so a
 * 12-month term understates every registration-scaled fee there - in Oregon, the
 * Oregon Registration Fee and the county fee ATC labels "Misc County Fees". Any state
 * not listed falls back to 12, which is the CULS Fees API documented default.
 *
 * Only states where the longer term is MANDATORY are listed. States that merely OFFER
 * a multi-year option - PA, VA, WV, VT, FL, OK, MO, OH, MI, AZ, NM, ID, MT, GA
 * (initial only), and AL (county-by-county) - still default to 12. Defaulting those to
 * 24 would double-quote every customer who takes the ordinary annual term.
 *
 * Deliberately excluded, both genuinely ambiguous:
 *   MD  24 was the standard, but since 2024-07-01 the customer picks 12 / 24 / 36 and
 *       MVA publishes no default (Md. Transp. 13-413(c) prices it linearly).
 *   DE  24 for an ordinary renewal, but DMV derives the term from model year plus
 *       inspection status, and a new car can run to 84 months (21 Del. C. 2109).
 *
 * Keyed on both the abbreviation and the full name, upper-cased, because callers send
 * either - the state value on both forms comes from ATC's zipcodeinfo response and can
 * be either form, the same ambiguity getRequiredMpgFieldsForState() already handles.
 *
 * This must stay in sync with every other copy of the table:
 *   DMV-bot-fees/lambda_function.py
 *   DMV-bot-refi-fees/lambda_function.py
 *   dmvnav/ATC_Flask/fees.py
 *   dmvnav/ATC_Flask/refi_fees.py
 *   dmvnav/Frontend/src/components/utility/registrationMonths.js  (this file)
 * or the bot and the web app quote different registration fees for the same vehicle.
 * dmvnav/tests/fees/test_fee_request_parity.py guards these copies.
 *
 * Sources, verified August 2026:
 *   AK 24  AS 28.10.108; dmv.alaska.gov - "Vehicles are registered for a two year
 *          period." One-year terms exist only on request, for commercial-use vehicles.
 *   CT 36  CGS 14-49(a) - "$120 every three years" for a passenger motor vehicle. The
 *          public CT DMV fee pages print "$120" with no term, so this one reads like
 *          an annual fee and is not. Registrants 65+ may elect one year.
 *   LA 24  La. R.S. 47:463 - collected "each two years in advance"; no 1-year election
 *          exists for passenger autos.
 *   MA 24  RMV schedule of fees, plate code PAN, renewal code B (biennial). Vanity
 *          plates (PAV) are annual, but those are not the standard passenger plate.
 *   NY 24  VTL 401(5)(c) - issued "for a period of not less than two years".
 *   OR 24  ORS 803.415 - "the registration period for any vehicle registered in this
 *          state ... is a biennial registration period."
 *   RI 24  dmv.ri.gov - "Most Passenger Vehicles (Registration every 2 years)."
 *   SC 24  S.C. Code 56-3-253(B) - "twenty-four consecutive months."
 */
export const STATE_REGISTRATION_MONTHS = {
  AK: '24', ALASKA: '24',
  CT: '36', CONNECTICUT: '36',
  LA: '24', LOUISIANA: '24',
  MA: '24', MASSACHUSETTS: '24',
  NY: '24', 'NEW YORK': '24',
  OR: '24', OREGON: '24',
  RI: '24', 'RHODE ISLAND': '24',
  SC: '24', 'SOUTH CAROLINA': '24',
};

/**
 * States that mandate a longer term for the INITIAL registration of a vehicle that
 * has never been titled. Applied only when the title status resolves to MSO; a used
 * vehicle ('T') always takes the ordinary term above.
 *
 *   NJ 48  N.J.S.A. 39:3-4 - "Every registration for new passenger automobiles shall
 *          expire ... in the 48th calendar month". NJ *renewals* revert to 12, which
 *          is why NJ is absent from STATE_REGISTRATION_MONTHS.
 *   OR 48  ORS 803.415(10); oregon.gov/odot/dmv - "NEW PASSENGER VEHICLE with a
 *          Manufacturer's Certificate of Origin: Receives four-year registration."
 *   TX 24  Tex. Transp. Code 502.044(a-1) - mandatory, not elective, but only for a
 *          vehicle "of the current or preceding model year" (see the gate below). TX
 *          renewals are 12, so TX is likewise absent from the table above.
 */
export const STATE_NEW_VEHICLE_MONTHS = {
  NJ: '48', 'NEW JERSEY': '48',
  OR: '48', OREGON: '48',
  TX: '24', TEXAS: '24',
};

/**
 * Texas is the one new-vehicle rule carrying a condition beyond "never titled":
 * 502.044(a-1) reaches only a current- or preceding-model-year vehicle. States listed
 * here get the new-vehicle term only when the model year passes that test; a missing
 * or unparseable model year falls back to the ordinary term rather than quoting high.
 */
export const MODEL_YEAR_GATED_NEW_VEHICLE_STATES = ['TX', 'TEXAS'];

export const DEFAULT_REGISTRATION_MONTHS = '12';

/**
 * Every registration term a state will actually issue for an ordinary passenger
 * vehicle, ascending. This is the picklist behind the Registration Months dropdown;
 * STATE_REGISTRATION_MONTHS above remains the single source of truth for which of
 * them is the default, and getSupportedRegistrationTerms() below enforces that the
 * default is always the first entry.
 *
 * Only states offering a CHOICE are listed. A state whose term is fixed - AK 24,
 * CT 36, LA/MA/NY/OR/RI/SC 24, and the 40-odd states that are simply 12 - needs no
 * entry: with nothing here the resolver returns the single default term, so the
 * dropdown correctly shows exactly one option.
 *
 * Terms SHORTER than the state's default are never listed, and are discarded if one
 * ever appears here, because the default is the state's mandated floor. That rule is
 * what keeps CT correct: CGS 14-49(a) does let a registrant aged 65 or older elect a
 * one-year term, but the app has no date of birth, and offering 12 would let the
 * common case be quoted at a third of the real fee. The same reasoning excludes the
 * commercial-use 1-year election in AK.
 *
 * Sources, verified August 2026:
 *   AL 12/24  Ala. Admin. Code r. 810-4-1-.25 - a county commission "may authorize an
 *             optional two-year registration renewal." Ala. Code 32-6-61 itself caps
 *             registration at "not exceeding 12 months," so 24 exists only where the
 *             county adopted it. No state-published list of adopting counties exists;
 *             24 is offered and left to the user, since 12 remains the default.
 *   AZ 12/24/60  azdot.gov - "Most vehicles can be registered for one, two, or five
 *             years, depending on emissions requirements." The 5-year term requires the
 *             vehicle be emissions-exempt for the whole cycle (outside the Phoenix and
 *             Tucson programs, MY1966 and older, never-registered new vehicles,
 *             alt-fuel).
 *   DE 12/24  21 Del. C. 2109(a) - "may be registered for 24 months, 12 months or 6
 *             months." DE is the one state where the term is DMV-assigned rather than
 *             customer-chosen: 2109 derives it from model year plus inspection, a used
 *             vehicle under 7 model years can run 6-72 months, and a new current-model
 *             -year vehicle up to 84 (2109(c)). Only the two ordinary renewal terms are
 *             offered; anything longer has to be quoted from the DMV's own figure.
 *   FL 12/24  Fla. Stat. 320.07(2)(b) - a private-use automobile "may renew the vehicle
 *             registration biennially."
 *   ID 12/24  I.C. 49-402B - "At the option of the applicant, any vehicle registered
 *             under ... 49-402(1) through (5) ... may be registered for a period of two
 *             (2) years." Note ITD's public pages do not advertise it.
 *   MD 12/24/36  mva.maryland.gov - "Registrations are available for a one-, two-, or
 *             three-year period." 24 was the standard until SB 362 added the one-year
 *             term on 2024-07-01, which is why MD has no default here and falls to 12.
 *   MI 12/24  michigan.gov/sos - "The two-year option is only available for RENEWING
 *             passenger vehicles, motorcycles, pickups and vans that are owned or
 *             leased by an individual" (HB 4117 of 2021). See STATE_NEW_VEHICLE_TERMS.
 *   MO 12/24  dor.mo.gov - biennial registration, gated on model-year parity: an
 *             even-model-year vehicle may go biennial only in an even calendar year,
 *             odd in odd. Not modelled - the parity rule would reject roughly half of
 *             all 24-month quotes and the customer learns it at the counter. (HB 2189
 *             of 2026 would have added a 60-month term and dropped the parity rule; it
 *             passed the House 143-0 and died in Senate committee. Not law.)
 *   MT 12/24  MCA 61-3-311 recognises a 12-month period and one "of at least 13 but
 *             less than 25 months." MCA 61-3-562 also allows PERMANENT registration of
 *             a light vehicle 11 years or older; that has no month value and cannot be
 *             sent as <RegistrationMonths>, so it is not offered.
 *   NM 12/24  mvd.newmexico.gov quotes one- and two-year fees for both original
 *             registration and renewal.
 *   OH 12/24/36/48/60  ORC 4503.103; bmv.ohio.gov - "A multi-year registration may
 *             extend from two to five years." Excludes IRP and buses.
 *   OK 12/24  Service Oklahoma - "you will have the option of either a 1-year or 2-year
 *             registration at the time of original registration and/or renewal,"
 *             effective 2023-11-01.
 *   PA 12/24  75 Pa.C.S. 1307 - the owner "may elect to pay an annual registration fee
 *             for a two-year period. The fee shall be two times the amount."
 *   VA 12/24/36  Va. Code 46.2-646; DMV VLIC-4.110 - 1, 2 or 3 years. The 3-year term
 *             is unavailable to an emissions-program vehicle more than 4 model years
 *             old, and mail renewals are capped at 2 years.
 *   VT 12/24  23 V.S.A. 305; dmv.vermont.gov publishes 1- and 2-year pleasure-car fees.
 *   WV 12/24  W. Va. Code 17A-3-16 - "the commissioner shall also offer an optional
 *             two-year registration system."
 */
export const STATE_REGISTRATION_TERMS = {
  AL: ['12', '24'], ALABAMA: ['12', '24'],
  AZ: ['12', '24', '60'], ARIZONA: ['12', '24', '60'],
  DE: ['12', '24'], DELAWARE: ['12', '24'],
  FL: ['12', '24'], FLORIDA: ['12', '24'],
  ID: ['12', '24'], IDAHO: ['12', '24'],
  MD: ['12', '24', '36'], MARYLAND: ['12', '24', '36'],
  MI: ['12', '24'], MICHIGAN: ['12', '24'],
  MO: ['12', '24'], MISSOURI: ['12', '24'],
  MT: ['12', '24'], MONTANA: ['12', '24'],
  NM: ['12', '24'], 'NEW MEXICO': ['12', '24'],
  OH: ['12', '24', '36', '48', '60'], OHIO: ['12', '24', '36', '48', '60'],
  OK: ['12', '24'], OKLAHOMA: ['12', '24'],
  PA: ['12', '24'], PENNSYLVANIA: ['12', '24'],
  VA: ['12', '24', '36'], VIRGINIA: ['12', '24', '36'],
  VT: ['12', '24'], VERMONT: ['12', '24'],
  WV: ['12', '24'], 'WEST VIRGINIA': ['12', '24'],
};

/**
 * Term picklist overrides for a vehicle that has never been titled, applied only when
 * the title status resolves to MSO. Listed here are the two states whose OPTION SET
 * changes with title status in a way the tables above cannot express:
 *
 *   GA 12/24  O.C.G.A. 40-2-20(d) - "the purchaser of a NEW motor vehicle which is a
 *             passenger car ... may choose to register such passenger car for an
 *             INITIAL period of two years," standard plate only. GA DOR: "After the
 *             initial two-years the renewal vehicles may only be renewed on an annual
 *             basis." GA is therefore 12-only on a used vehicle and gains 24 on an MSO,
 *             the reverse of the usual pattern - hence no GA row in the table above.
 *   MI 12     The Michigan 2-year term is renewal-only, so an MSO drops back to 12.
 *
 * NJ, OR and TX need no row: their new-vehicle term is mandatory and single, so the
 * value already in STATE_NEW_VEHICLE_MONTHS is the whole picklist, model-year gate and
 * all. TX in particular must keep flowing through that gate - an MSO that is not of the
 * current or preceding model year is an ordinary 12-month TX registration, not 24.
 */
export const STATE_NEW_VEHICLE_TERMS = {
  GA: ['12', '24'], GEORGIA: ['12', '24'],
  MI: ['12'], MICHIGAN: ['12'],
};

/**
 * Vehicle-type buckets used by the scope tables below.
 *
 * Keyed on the LABELS the forms hold (`vehicleInfo.vehicleType` in redux, and the
 * `<option value>` on the Jinja pages) and on the ATC codes from VEHICLE_TYPE_MAPPER,
 * so either can be passed in.
 *
 * HT is deliberately separate from LT. VEHICLE_TYPE_MAPPER collapses 'Truck' into 'LT'
 * for the ATC request, but a heavy truck and a pickup are on different registration
 * cycles in most states - so the term logic must keep them apart even though the wire
 * format does not. That is also why passing the ATC code 'LT' alone cannot distinguish
 * them: the forms pass the label, which can.
 */
const VEHICLE_TYPE_BUCKETS = {
  'PASSENGER VEHICLE': 'PV', PV: 'PV',
  'SPORTS UTILITY VEHICLE': 'SUV', 'MULTIPURPOSE PASSENGER VEHICLE': 'SUV', SUV: 'SUV',
  'LIGHT TRUCK': 'LT', 'PICKUP TRUCK': 'LT', LT: 'LT',
  TRUCK: 'HT',
  MOTORCYCLE: 'MC', M: 'MC',
  TRAILER: 'TR',
  MOTORHOME: 'MH', O: 'MH',
  'CAB & CHASSIS': 'CC', CC: 'CC',
};

/**
 * Vehicle types that may NOT receive a term longer than 12 months, per state.
 *
 * The tables above answer "how long does this state register a vehicle for"; this one
 * answers "which vehicles did that rule ever mean". Most of the mandatory longer terms
 * are written for a "passenger motor vehicle" and leave motorcycles, trailers and
 * commercial trucks on an ordinary annual cycle - so applying the state's headline term
 * to every vehicle type over-quotes those, in some cases at double or triple the real
 * fee.
 *
 * A state absent from this table applies its term to every type, which is the behaviour
 * that shipped before. An unknown or blank vehicle type is also treated as in scope, so
 * nothing changes until the user has actually chosen a type.
 *
 * ONLY exclusions are listed - cases where the correct term is SHORTER than what the
 * tables above would produce. Research also turned up several states where a type
 * should get a LONGER term than we currently send (LA light trucks and motorcycles at
 * 48, OR new light trailers at 48, DE new vehicles up to 84, OH commercial trailers up
 * to 84, MA semi-trailers at 60). Those are deliberately NOT encoded here: an exclusion
 * can only move a quote toward the documented API default of 12, while an increase
 * moves real money in the other direction and wants sign-off first.
 *
 * Sources, verified August 2026:
 *   CT HT   CGS 14-49(h) - "The minimum annual fee for any commercial registration";
 *           the 36-month triennial is 14-49(a), passenger only. Motorcycles ARE
 *           triennial (14-49(b)(1), $63) and campers/motorhomes too (14-49(t)), so
 *           only the commercial class is excluded.
 *   MA MC/TR/MH/HT  RMV Schedule of Fees carries an explicit renewal-term code per
 *           plate class: passenger PAN is 'B' (biennial), while motorcycle MCN,
 *           trailer TRN and auto-home AHN are all 'A' (annual), as is commercial CON
 *           above 5,000 lb. This is the clearest of the eight.
 *   NY MC   nydmv - "Motorcycles are registered for one year"; VTL 401(8)(a) makes
 *           trailers an annual fee, and 401(5)(c) caps the 2-year rule at vehicles of
 *           "not more than eighteen thousand pounds" maximum gross weight.
 *   OR HT   ORS 803.415(1)(a)-(b) - vehicles registered by weight are "annual or
 *           quarterly", not biennial.
 *   SC HT   S.C. Code 56-3-376(3) - motor carrier vehicles are "assigned a staggered
 *           annual registration".
 *   LA HT   La. R.S. 47:508(A)(3)-(4) - trucks over 10,000 lb "expire annually".
 *   FL HT   Fla. Stat. 320.07(2)(b) enumerates the biennial-eligible fee classes and
 *           stops at 320.08(4)(b), i.e. 7,999 lb GVW.
 *   AZ HT   ADOT - vehicles over 55,000 lb GVW are not eligible for multi-year.
 *   OH HT   ORC 4503.103(A)(4) excludes IRP-apportioned vehicles, commercial cars in
 *           intrastate commerce, and buses.
 *   VT HT   VT DMV fee schedule - the two-year column covers trucks under 6,100 lb
 *           only; heavier is one-year.
 *   WV HT   W. Va. Code 17A-10-1 - Class B (trucks over 10,000 lb) gets no two-year
 *           grant; 17A-3-16 extends the option to Class A and Class G only.
 *   MO HT/TR  RSMo 301.147 - biennial for motor vehicles "other than commercial motor
 *           vehicles licensed in excess of 54,000 lbs"; trailers are not motor
 *           vehicles and are priced 1 / 3 year / permanent instead.
 *   NM HT   NMSA 66-3-20.1 - two-year registration reaches trucks "with a declared
 *           gross weight of 26,000 pounds or less".
 *   VA HT   Va. Code 46.2-646 - excludes vehicles at or above 55,000 lb and IRP.
 *   ID HT/TR  I.C. 49-402B(3) excludes 49-435 (IRP) vehicles; trailers sit in
 *           49-434(3) with a separate 1 / 5 / 10-year scheme, not 12/24.
 *   MI HT/TR  michigan.gov/sos - the two-year option covers "passenger vehicles,
 *           motorcycles, pickups and vans owned or leased by an individual", which is
 *           the birthday expiration scheme; commercial and business-owned vehicles
 *           expire the last day of February and sit outside it.
 *   MD HT   COMAR 11.15.12.05 - classes at 55,000 lb and above "shall be renewed
 *           annually".
 *   PA TR   PennDOT MV-70S prints One-Year / 5-Year / Permanent columns for trailers
 *           and no two-year column at all.
 *   MT MC/TR/MH/HT  MCA 61-3-321(3) and (8) put trailers and motorcycles on MANDATORY
 *           permanent registration - "all trailers must be permanently registered",
 *           "all street-legal motorcycles and quadricycles must be registered
 *           permanently". Permanent has no month value and cannot be sent as
 *           <RegistrationMonths> at all, so the honest option here is the 12 fallback
 *           rather than an invented number; see the note on Montana above. Motor homes
 *           are annual under 61-3-321(7).
 *
 * Not excluded, deliberately: AK and RI. Alaska's divergences land on trailers and
 * commercial-use vehicles that this form cannot select, and the Rhode Island findings
 * for pickups and trailers rest on a fee table that marks the two-year classes by
 * omission rather than statement - not firm enough to move a quote on.
 */
export const STATE_LONG_TERM_EXCLUDED_TYPES = {
  AZ: ['HT'], ARIZONA: ['HT'],
  CT: ['HT'], CONNECTICUT: ['HT'],
  FL: ['HT'], FLORIDA: ['HT'],
  ID: ['HT', 'TR'], IDAHO: ['HT', 'TR'],
  LA: ['HT'], LOUISIANA: ['HT'],
  MA: ['MC', 'TR', 'MH', 'HT'], MASSACHUSETTS: ['MC', 'TR', 'MH', 'HT'],
  MD: ['HT'], MARYLAND: ['HT'],
  MI: ['HT', 'TR'], MICHIGAN: ['HT', 'TR'],
  MO: ['HT', 'TR'], MISSOURI: ['HT', 'TR'],
  MT: ['MC', 'TR', 'MH', 'HT'], MONTANA: ['MC', 'TR', 'MH', 'HT'],
  NM: ['HT'], 'NEW MEXICO': ['HT'],
  NY: ['MC', 'TR', 'HT'], 'NEW YORK': ['MC', 'TR', 'HT'],
  OH: ['HT'], OHIO: ['HT'],
  OR: ['HT'], OREGON: ['HT'],
  PA: ['TR'], PENNSYLVANIA: ['TR'],
  SC: ['HT'], 'SOUTH CAROLINA': ['HT'],
  VA: ['HT'], VIRGINIA: ['HT'],
  VT: ['HT'], VERMONT: ['HT'],
  WV: ['HT'], 'WEST VIRGINIA': ['HT'],
};

/**
 * Vehicle types that may NOT receive the longer INITIAL term for a never-titled
 * vehicle. Excluded types fall through to the state's ordinary rules rather than
 * straight to 12 - an Oregon motorcycle drops from the 48-month new-vehicle term to
 * Oregon's ordinary 24, not to 12.
 *
 *   NJ MC/HT/TR/MH  N.J.S.A. 39:1-1 defines "automobile" as "all motor vehicles except
 *           motorcycles", and 39:3-4's four-year rule reaches "new passenger
 *           automobiles" only. Commercial vehicles register under 39:3-20, which
 *           expires "on the last day of the twelfth calendar month". MVC's consumer
 *           page loosely says "New vehicles that have never been titled will receive a
 *           four-year registration", which is almost certainly where the over-broad
 *           reading came from.
 *   OR MC/HT/MH  ORS 803.415(10) now has exactly two paragraphs - new vehicles under
 *           803.420(6)(a) and new trailers under 803.420(6)(b). Enrolled HB 2100
 *           (2023) struck the motorcycle/moped paragraph. NOTE: ODOT's own Title &
 *           Registration Handbook Chapter L still describes a four-year term for new
 *           motorcycles with an MCO, so statute and manual disagree; the statute is
 *           followed here and the conflict is worth a call to ODOT.
 *   TX MC/HT/TR/MH/CC  Tex. Transp. Code 502.044(a-1) reaches "a passenger car or
 *           light truck" only. "Light truck" is 541.201: carrying capacity of 2,000 lb
 *           or less - so a heavy-duty pickup can fall out of it, which this table
 *           cannot see. SUVs are safe either way, satisfying "passenger car".
 *   GA LT/HT/MC/TR/MH/CC  O.C.G.A. 40-2-20(d) - "a new motor vehicle which is a
 *           passenger car", standard plate only. GA DOR expressly excludes motorcycles
 *           among others, and trucks are excluded by not being passenger cars.
 */
export const STATE_NEW_VEHICLE_EXCLUDED_TYPES = {
  GA: ['LT', 'HT', 'MC', 'TR', 'MH', 'CC'],
  GEORGIA: ['LT', 'HT', 'MC', 'TR', 'MH', 'CC'],
  NJ: ['MC', 'HT', 'TR', 'MH'], 'NEW JERSEY': ['MC', 'HT', 'TR', 'MH'],
  OR: ['MC', 'HT', 'MH'], OREGON: ['MC', 'HT', 'MH'],
  TX: ['MC', 'HT', 'TR', 'MH', 'CC'], TEXAS: ['MC', 'HT', 'TR', 'MH', 'CC'],
};

/**
 * Bucket a vehicle type label or ATC code. Returns '' for blank or unrecognised
 * input, which every caller treats as "no restriction" - the term rules must not
 * start excluding things because a new vehicle type was added to a form.
 *
 * @param {string} vehicleType
 * @returns {string} one of PV / SUV / LT / HT / MC / TR / MH / CC, or ''
 */
export const getVehicleTypeBucket = (vehicleType) =>
  VEHICLE_TYPE_BUCKETS[String(vehicleType ?? '').trim().toUpperCase()] || '';

/**
 * True when this vehicle type may take a term longer than 12 months in this state.
 *
 * @param {string} state
 * @param {string} [vehicleType]
 * @returns {boolean}
 */
const allowsLongTerm = (stateKey, vehicleType) => {
  const bucket = getVehicleTypeBucket(vehicleType);
  if (!bucket) return true;
  const excluded = STATE_LONG_TERM_EXCLUDED_TYPES[stateKey];
  return !excluded || excluded.indexOf(bucket) === -1;
};

/**
 * True when this vehicle type may take the longer new-vehicle term in this state.
 *
 * @param {string} stateKey
 * @param {string} [vehicleType]
 * @returns {boolean}
 */
const allowsNewVehicleTerm = (stateKey, vehicleType) => {
  const bucket = getVehicleTypeBucket(vehicleType);
  if (!bucket) return true;
  const excluded = STATE_NEW_VEHICLE_EXCLUDED_TYPES[stateKey];
  return !excluded || excluded.indexOf(bucket) === -1;
};

/**
 * Title status values meaning "new, never titled". 'MSO' is the ATC code the online
 * app and both fees Lambdas send; the long forms are what the UIs display.
 */
const NEW_TITLE_STATUSES = ['mso', 'new', 'new vehicle', 'mso (new vehicle)'];

/**
 * True when the title status means the vehicle has never been titled.
 *
 * @param {string} titleStatus - 'MSO' / 'T', or the displayed long form.
 * @returns {boolean}
 */
const isNewVehicle = (titleStatus) =>
  NEW_TITLE_STATUSES.indexOf(String(titleStatus || '').trim().toLowerCase()) !== -1;

/**
 * True when the model year is last year, this year, or next year.
 *
 * Manufacturers ship next-model-year vehicles during the current calendar year, so a
 * 2027 car sold in 2026 still counts as current model year for the Texas rule. An
 * unparseable or missing model year returns false, dropping the caller back to the
 * ordinary term.
 *
 * @param {string|number} modelYear
 * @returns {boolean}
 */
const isCurrentOrPrecedingModelYear = (modelYear) => {
  const year = parseInt(String(modelYear ?? '').trim(), 10);
  if (Number.isNaN(year)) return false;
  const current = new Date().getFullYear();
  return year >= current - 1 && year <= current + 1;
};

/**
 * The registration term to send when the user hasn't specified one.
 *
 * @param {string} state - state abbreviation or full name, any case.
 * @param {string} [titleStatus] - 'MSO' (new) or 'T' (used); omit when unknown.
 * @param {string|number} [modelYear] - only consulted for the Texas new-vehicle gate.
 * @returns {string} months, as a string, ready for the request payload.
 */
export const getDefaultRegistrationMonths = (state, titleStatus, modelYear, vehicleType) => {
  const key = String(state || '').trim().toUpperCase();
  if (isNewVehicle(titleStatus) && allowsNewVehicleTerm(key, vehicleType)) {
    const months = STATE_NEW_VEHICLE_MONTHS[key];
    if (months
      && (MODEL_YEAR_GATED_NEW_VEHICLE_STATES.indexOf(key) === -1
        || isCurrentOrPrecedingModelYear(modelYear))) {
      return months;
    }
  }
  // A type outside the state's long-term scope falls back to the documented API
  // default rather than the state's headline term.
  if (!allowsLongTerm(key, vehicleType)) return DEFAULT_REGISTRATION_MONTHS;
  return STATE_REGISTRATION_MONTHS[key] || DEFAULT_REGISTRATION_MONTHS;
};

/**
 * Parse a term to a number for comparison and sorting. '' / undefined / junk sort
 * last rather than ahead of every real term, so a malformed table entry can never
 * become the selected default.
 */
const toMonths = (term) => {
  const months = parseInt(String(term ?? '').trim(), 10);
  return Number.isNaN(months) ? Number.MAX_SAFE_INTEGER : months;
};

/**
 * Every registration term the state will issue for this vehicle, ascending.
 *
 * Guarantees, both relied on by the Registration Months dropdown:
 *   - the list is never empty;
 *   - `result[0] === getDefaultRegistrationMonths(state, titleStatus, modelYear)`.
 *
 * The second one is structural rather than a property of the tables. The default is
 * treated as the state's floor: anything shorter is filtered out of the picklist, and
 * the default is appended if the picklist somehow omits it. So the two tables can
 * never drift into a state where the dropdown's first entry disagrees with the term
 * fees/api.js and _default_registration_months() would have sent on their own, and a
 * mistake in STATE_REGISTRATION_TERMS can only ever add or remove a longer option -
 * it cannot quote a state below its mandated minimum.
 *
 * With no state resolved the caller has not chosen anything yet; this returns the bare
 * ['12'] so the dropdown has something to show, and the form layer
 * (utility/registrationMonthsField.js) is what declines to select it.
 *
 * @param {string} state - state abbreviation or full name, any case.
 * @param {string} [titleStatus] - 'MSO' (new) or 'T' (used); omit when unknown.
 * @param {string|number} [modelYear] - only consulted for the Texas new-vehicle gate.
 * @returns {string[]} months, as strings, ascending, default first.
 */
export const getSupportedRegistrationTerms = (state, titleStatus, modelYear, vehicleType) => {
  const key = String(state || '').trim().toUpperCase();
  const defaultTerm = getDefaultRegistrationMonths(state, titleStatus, modelYear, vehicleType);
  const floor = toMonths(defaultTerm);

  // A type outside the state's long-term scope is offered nothing but the default. The
  // elective menus above are all written for ordinary passenger registrations, so a
  // heavy truck in Ohio should not be shown 24/36/48/60 any more than it should default
  // to them.
  if (!allowsLongTerm(key, vehicleType)) return [defaultTerm];

  const offered = (isNewVehicle(titleStatus)
      && allowsNewVehicleTerm(key, vehicleType)
      && STATE_NEW_VEHICLE_TERMS[key])
    || STATE_REGISTRATION_TERMS[key]
    || [];

  const terms = offered.filter((term) => toMonths(term) >= floor);
  if (terms.indexOf(defaultTerm) === -1) terms.push(defaultTerm);

  return terms
    .filter((term, index) => terms.indexOf(term) === index)
    .sort((a, b) => toMonths(a) - toMonths(b));
};
